import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Select,
  Button,
  Table,
  message,
  Spin,
  Tabs,
  Card,
  Row,
  Col,
  Typography,
  Space,
  DatePicker,
  Empty,
  Divider,
} from "antd";
import {
  SendOutlined,
  BarChartOutlined,
  LineChartOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  ReloadOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import dayjs from "dayjs";
import "../../css/sm-art-common.css";
import CriticalOperationService from "../../services/CriticalOperationService";

const { Option } = Select;
const { TabPane } = Tabs;
const { Title, Text } = Typography;

const HourlyProductionReport = () => {
  const [lines, setLines] = useState([]);
  const [operations, setOperations] = useState([]);
  const [line, setLine] = useState("");
  const [operation, setOperation] = useState(undefined);

  const [tableData, setTableData] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);
  const [uniqueOperations, setUniqueOperations] = useState([]);

  const [loading, setLoading] = useState(false);
  const [mastersLoading, setMastersLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [activeTab, setActiveTab] = useState("1");

  const isMountedRef = useRef(true);

  const dropdownSpinner = (
    <div style={{ textAlign: "center", padding: 8 }}>
      <Spin size="small" />
    </div>
  );

  useEffect(() => {
    isMountedRef.current = true;

    (async () => {
      try {
        setMastersLoading(true);

        const [lineRes, opRes] = await Promise.all([
          CriticalOperationService.getLines(),
          CriticalOperationService.getOperations(),
        ]);

        const lineList =
          lineRes.ok && lineRes.data?.status ? lineRes.data?.line_no || [] : [];

        const opList =
          opRes.ok && opRes.data?.status ? opRes.data?.data || [] : [];

        if (!isMountedRef.current) return;

        setLines(Array.isArray(lineList) ? lineList : []);
        setOperations(Array.isArray(opList) ? opList : []);
      } catch (e) {
        console.error(e);
        if (!isMountedRef.current) return;
        setLines([]);
        setOperations([]);
      } finally {
        if (isMountedRef.current) setMastersLoading(false);
      }
    })();

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const getColor = (i) => {
    const colors = [
      "#1890ff",
      "#52c41a",
      "#faad14",
      "#f5222d",
      "#13c2c2",
      "#722ed1",
      "#eb2f96",
      "#a0d911",
      "#fa8c16",
    ];
    return colors[i % colors.length];
  };

const columns = useMemo(
  () => [
    {
      title: "Device ID",
      dataIndex: "device_id",
      align: "center",
      width: 140,
    },
    {
      title: "Operation Name",
      dataIndex: "op_name",
      align: "left",
      width: 200,
    },
    {
      title: "Day Target",
      dataIndex: "target_pcs",
      align: "center",
      width: 120,
      render: (value) => Number(value || 0),
    },
    {
      title: "Real Time Production",
      dataIndex: "operation_count",
      align: "center",
      width: 170,
      render: (value) => Number(value || 0),
    },
    {
      title: "Gap",
      key: "gap",
      align: "center",
      width: 120,
      render: (_, record) => {
        const realTimeProduction = Number(
          record?.operation_count || 0
        );

        const dayTarget = Number(
          record?.target_pcs || 0
        );

        const gap = realTimeProduction - dayTarget;

        return (
          <span
            style={{
              fontWeight: 600,
              color:
                gap > 0
                  ? "#16a34a"
                  : gap < 0
                  ? "#dc2626"
                  : "#475569",
            }}
          >
            {gap > 0 ? `+${gap}` : gap}
          </span>
        );
      },
    },
    {
      title: "Projected Production",
      dataIndex: "Projected Production",
      align: "center",
      width: 170,
      render: (value) => Number(value || 0),
    },
    {
      title: "Real Time Target",
      dataIndex: "Real time Target",
      align: "center",
      width: 150,
      render: (value) => Number(value || 0),
    },
  ],
  []
);

  const labelMap = useMemo(
    () => ({
      target_pcs: "Day Target",
      "Real time Target": "Real Time Target",
      "Projected Production": "Projected Production",
      operation_count: "Real Time Production",
    }),
    []
  );

  const fetchReportData = useCallback(async () => {
    if (!line) return;

    const res = await CriticalOperationService.getMasterOperationsCount(
      line,
      operation || "",
      selectedDate.format("YYYY-MM-DD")
    );

    if (!isMountedRef.current) return;

    if (!res.ok) {
      setTableData([]);
      return;
    }

    const body = res.data;

    if (body?.status) {
      const parsedOps =
        body?.data?.master_operations?.map((item) => ({
          ...item,
          target_pcs: parseInt(item.target_pcs || 0, 10),
          operation_count: parseInt(item.operation_count || 0, 10),
          "Projected Production": parseInt(
            parseFloat(item["Projected Production"] || 0),
            10
          ),
          "Real time Target": parseInt(
            parseFloat(item["Real time Target"] || 0),
            10
          ),
        })) || [];

      setTableData(parsedOps);
    } else {
      setTableData([]);
    }
  }, [line, operation, selectedDate]);

  const fetchHourlyData = useCallback(async () => {
    if (!line) return;

    const res = await CriticalOperationService.getHourlyDeviceCount(
      line,
      selectedDate.format("YYYY-MM-DD")
    );

    if (!isMountedRef.current) return;

    if (!res.ok) {
      setHourlyData([]);
      setUniqueOperations([]);
      return;
    }

    const body = res.data;
    const payload = body?.data;

    if (body?.status && payload?.hourly_data) {
      const opNames = new Set();
      payload.hourly_data.forEach((hour) =>
        hour.devices.forEach((d) => opNames.add(d.op_name))
      );
      const allOps = Array.from(opNames);

      const formatted = payload.hourly_data.map((hour) => {
        const obj = { hour_display: hour.hour_display };
        allOps.forEach((op) => (obj[op] = null));
        hour.devices.forEach((d) => {
          obj[d.op_name] = d.cumulative_count;
        });
        return obj;
      });

      setHourlyData(formatted);
      setUniqueOperations(allOps);
    } else {
      setHourlyData([]);
      setUniqueOperations([]);
    }
  }, [line, selectedDate]);

  const loadAll = useCallback(async () => {
    if (!line) return;
    setLoading(true);
    try {
      await Promise.all([fetchReportData(), fetchHourlyData()]);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [line, fetchReportData, fetchHourlyData]);

  useEffect(() => {
    if (!line) return;

    loadAll();

    const shouldRefresh = activeTab === "1" || activeTab === "2";
    if (!shouldRefresh) return;

    const interval = setInterval(() => {
      loadAll();
    }, 5000);

    return () => clearInterval(interval);
  }, [line, operation, selectedDate, activeTab, loadAll]);

  const handleLoad = () => {
    if (!line) return message.warning("Please select a Line!");
    loadAll();
  };

  const handleReset = () => {
    setLine("");
    setOperation(undefined);
    setTableData([]);
    setHourlyData([]);
    setUniqueOperations([]);
    setSelectedDate(dayjs());
  };

  const quickToday = () => setSelectedDate(dayjs());
  const quickLast7 = () => setSelectedDate(dayjs().subtract(6, "day"));
  const quickThisMonth = () => setSelectedDate(dayjs().startOf("month"));

  return (
    <div className="sm-page">
      <style>{`
        .sm-select.ant-select .ant-select-arrow,
        .sm-select.ant-select .ant-select-suffix,
        .sm-select.ant-select .ant-select-arrow *,
        .sm-select.ant-select .ant-select-suffix * {
          color: #000 !important;
          fill: #000 !important;
        }
      `}</style>

      <div className="sm-page-inner">
        <div className="sm-header">
          <div className="sm-title-wrap">
            <Title level={3} className="sm-page-title">
              <ThunderboltOutlined style={{ color: "#2150FD" }} />
              Hourly Production Report
            </Title>
            <Text className="sm-page-subtitle">
              Device-wise critical operations grouped by line.
            </Text>
          </div>

          <div className="sm-top-actions">
            <Button className="sm-btn-soft" icon={<CalendarOutlined />} onClick={quickToday}>
              Today
            </Button>
            <Button className="sm-btn-soft" onClick={quickLast7}>
              Last 7 Days
            </Button>
            <Button className="sm-btn-soft" onClick={quickThisMonth}>
              This Month
            </Button>
          </div>
        </div>

        <Card
          className="sm-card"
          title={<span>Filters</span>}
          extra={
            <div className="sm-filter-actions">
              <Button className="sm-btn-soft" icon={<ReloadOutlined />} onClick={handleReset}>
                Reset
              </Button>
              <Button
                className="sm-btn-primary"
                icon={<SendOutlined />}
                loading={loading}
                onClick={handleLoad}
              >
                Load
              </Button>
            </div>
          }
        >
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={8}>
              <label className="sm-label">Select Line</label>
              <Select
                className="sm-select"
                placeholder="Select Line"
                value={line || undefined}
                onChange={setLine}
                showSearch
                optionFilterProp="children"
                loading={mastersLoading}
                notFoundContent={mastersLoading ? dropdownSpinner : "No data"}
              >
                {lines.map((ln) => (
                  <Option key={ln} value={ln}>
                    {ln}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} md={10}>
              <label className="sm-label">Select Operation</label>
              <Select
                className="sm-select"
                placeholder="Select Operation"
                value={operation}
                onChange={setOperation}
                showSearch
                optionFilterProp="children"
                allowClear
                loading={mastersLoading}
                notFoundContent={mastersLoading ? dropdownSpinner : "No data"}
              >
                {operations.map((op) => (
                  <Option key={op.op_id} value={op.op_id}>
                    {op.op_name}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} md={6}>
              <label className="sm-label">Select Date</label>
              <DatePicker
                className="sm-date-picker"
                value={selectedDate}
                onChange={(d) => setSelectedDate(d || dayjs())}
                format="DD-MM-YYYY"
                allowClear={false}
              />
            </Col>
          </Row>

          <Divider style={{ margin: "14px 0 10px" }} />

          <Space wrap>
            <Button className="sm-btn-soft" onClick={() => setSelectedDate(dayjs())}>
              Today
            </Button>
            <Button
              className="sm-btn-soft"
              onClick={() => setSelectedDate(dayjs().subtract(1, "day"))}
            >
              Yesterday
            </Button>
            <Button
              className="sm-btn-soft"
              onClick={() => setSelectedDate(dayjs().startOf("month"))}
            >
              This Month Start
            </Button>
          </Space>
        </Card>

        <Card className="sm-card" style={{ marginTop: 16 }}>
          <Tabs activeKey={activeTab} onChange={setActiveTab} centered>
            <TabPane
              tab={
                <span>
                  <BarChartOutlined /> Overall Production
                </span>
              }
              key="1"
            >
              <Spin spinning={loading}>
                {!tableData?.length ? (
                  <div className="sm-empty-wrap">
                    <Empty
                      description={
                        <span className="sm-empty-text">
                          No data found. Choose filters and click Load.
                        </span>
                      }
                    />
                  </div>
                ) : (
                  <Card
                    className="sm-card"
                    style={{ boxShadow: "none", marginBottom: 0 }}
                    bodyStyle={{ padding: 10 }}
                  >
                    <ResponsiveContainer width="100%" height={420}>
                      <BarChart
                        data={tableData}
                        margin={{ top: 20, right: 25, left: 0, bottom: 10 }}
                        barCategoryGap="20%"
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="op_name" />
                        <YAxis domain={[0, "dataMax"]} allowDecimals={false} />
                        <RTooltip />
                        <Legend formatter={(value) => labelMap[value] || value} />

                        <Bar dataKey="target_pcs" fill="#F7CB72FF" barSize={120} stackId="plan">
                          <LabelList dataKey="target_pcs" position="insideTop" fill="#000" />
                        </Bar>
                        <Bar
                          dataKey="Projected Production"
                          fill="#85B5E2FF"
                          barSize={120}
                          stackId="plan"
                        >
                          <LabelList
                            dataKey="Projected Production"
                            position="insideTop"
                            fill="#fff"
                          />
                        </Bar>
                        <Bar
                          dataKey="Real time Target"
                          fill="#EE878DFF"
                          barSize={120}
                          stackId="actual"
                        >
                          <LabelList
                            dataKey="Real time Target"
                            position="insideTop"
                            fill="#fff"
                          />
                        </Bar>
                        <Bar
                          dataKey="operation_count"
                          fill="#8EDD67FF"
                          barSize={120}
                          stackId="actual"
                        >
                          <LabelList
                            dataKey="operation_count"
                            position="insideTop"
                            fill="#fff"
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                )}
              </Spin>
            </TabPane>

            <TabPane
              tab={
                <span>
                  <LineChartOutlined /> Hourly Production
                </span>
              }
              key="2"
            >
              <Spin spinning={loading}>
                {!hourlyData?.length ? (
                  <div className="sm-empty-wrap">
                    <Empty
                      description={
                        <span className="sm-empty-text">
                          No hourly data. Choose filters and click Load.
                        </span>
                      }
                    />
                  </div>
                ) : (
                  <Card
                    className="sm-card"
                    style={{ boxShadow: "none", marginBottom: 0 }}
                    bodyStyle={{ padding: 10 }}
                  >
                    <ResponsiveContainer width="100%" height={420}>
                      <LineChart
                        data={hourlyData}
                        margin={{ top: 30, right: 40, left: 10, bottom: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="hour_display" />
                        <YAxis />
                        <RTooltip />
                        <Legend />
                        {uniqueOperations.map((op, i) => (
                          <Line
                            key={op}
                            type="monotone"
                            dataKey={op}
                            stroke={getColor(i)}
                            strokeWidth={3}
                            connectNulls={false}
                            dot={{ r: 4 }}
                            name={op}
                          >
                            <LabelList
                              dataKey={op}
                              position="top"
                              offset={10}
                              fontSize={12}
                              fill="#000"
                            />
                          </Line>
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Card>
                )}
              </Spin>
            </TabPane>

            <TabPane
              tab={
                <span>
                  <FileTextOutlined /> Summary
                </span>
              }
              key="3"
            >
              <Spin spinning={loading}>
                {!tableData?.length ? (
                  <div className="sm-empty-wrap">
                    <Empty
                      description={
                        <span className="sm-empty-text">
                          No summary data found.
                        </span>
                      }
                    />
                  </div>
                ) : (
                  <div className="sm-table-wrap">
                    <Table
                      className="sm-table"
                      dataSource={tableData}
                      columns={columns}
                      bordered
                      size="middle"
                      sticky
                      scroll={{ x: 1000, y: 420 }}
                      pagination={{
                        pageSize: 10,
                        showSizeChanger: false,
                        position: ["bottomCenter"],
                        className: "sm-pagination",
                      }}
                      rowKey={(r) => r.com_id || r.device_id || `${r.op_id}-${r.op_name}`}
                    />
                  </div>
                )}
              </Spin>
            </TabPane>
          </Tabs>
        </Card>
      </div>
    </div>
  );
};

export default HourlyProductionReport;